<template>
  <div class="player" :class="doc.controls.playerClass">
    <div class="container">
      <div
        v-if="
          playbackControl &&
          chapterControl &&
          progressControl &&
          rateControl &&
          volumeControl
        "
        class="player__content"
      >
        <div class="player__control">
          <PlayerVolume
            :volumeControl="volumeControl"
            class="player-volume--pc"
          />
          <div class="player-play">
            <button
              class="player-play__link player-play__link--backward"
              :disabled="!playbackControl.audioLoaded"
              @click="playbackControl.backward()"
            >
              <span class="iconfont iplayer-reduce" />
            </button>
            <button
              v-if="isBook"
              class="player-play__link player-play__link--prev"
              :disabled="!chapterControl.hasPrev()"
              @click="chapterControl.prev()"
            >
              <span class="iconfont iplayer-prev" />
            </button>
            <button
              class="player-play__link player-play__link--play"
              @click="togglePlayback()"
            >
              <circle-spinner
                v-if="
                  !playbackControl.paused &&
                  playbackControl.interruptedByNetwork
                "
                :animation-duration="2000"
                :size="58"
                :color="'#ffc906'"
              />
              <span
                v-else
                class="iconfont"
                :class="{
                  'iplayer-pause': !playbackControl.paused,
                  'iplayer-play': playbackControl.paused,
                }"
              />
            </button>
            <button
              v-if="isBook"
              class="player-play__link player-play__link--next"
              :disabled="!chapterControl.hasNext()"
              @click="chapterControl.next()"
            >
              <span class="iconfont iplayer-next" />
            </button>
            <button
              class="player-play__link player-play__link--forward"
              :disabled="!playbackControl.audioLoaded"
              @click="playbackControl.forward()"
            >
              <span class="iconfont iplayer-add" />
            </button>
          </div>
          <PlayerRate :rateControl="rateControl" class="player-speed--pc" />
        </div>
        <PlayerChapterProgress
          :progressControl="progressControl"
          :chapterControl="chapterControl"
        />
        <div class="player__control-m">
          <PlayerVolume :volumeControl="volumeControl" />
          <PlayerRate :rateControl="rateControl" />
        </div>
      </div>
      <div v-else class="player--loading">
        <circle-spinner
          :animation-duration="2000"
          :size="36"
          :color="'#ffc906'"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import CircleSpinner from '@/components/ui/CircleSpinner.vue'

import { playerSession } from '@/services/player'
import { assertNotNull } from '@/helpers/typing'
import { miniPlayer19040Variant } from '@/services/ab'

import PlayerChapterProgress from './ChapterProgress.vue'
import PlayerVolume from './Volume.vue'
import PlayerRate from './Rate.vue'
import type { DocView } from '@/models/doc'

const props = defineProps<{
  doc: DocView
}>()

const router = useRouter()
const route = useRoute()

// The audio element and the controls belong to the global player session, so
// that the playback survives switching to the Mini Player, see #19040.
const playbackControl = computed(() => playerSession.value.playbackControl)
const chapterControl = computed(() => playerSession.value.chapterControl)
const progressControl = computed(() => playerSession.value.progressControl)
const rateControl = computed(() => playerSession.value.rateControl)
const volumeControl = computed(() => playerSession.value.volumeControl)

onMounted(async () => {
  await playerSession.value.start(
    props.doc,
    // Follow the playback with the reading page: when the audio reaches the
    // next chapter, navigate to it.
    (chapterIdx: number) => {
      router.push(props.doc.pageLink(chapterIdx).params)
    },
    props.doc.pageNum,
  )

  // Hook chapter control to doc.pageNum changes, so when we navigate
  // the text chapters, the playback follows.
  watch(
    () => props.doc.pageNum,
    (newPageNum: number) => {
      assertNotNull(chapterControl.value).go(newPageNum)
    },
  )
})

onUnmounted(async () => {
  if (miniPlayer19040Variant() === 'B') {
    // Keep the player session alive, so that it can be taken over by the Mini Player,
    // but remove the chapter change callback (it updates the current route,
    // which we don't want in the Mini Player).
    playerSession.value.setChapterChangeCallback(() => {})
  } else {
    // If there's no Mini Player, we just stop the player session.
    await playerSession.value.stop()
  }
})

watch(
  () => playerSession.value.isPlaying,
  (isPlaying: boolean) => {
    props.doc.isAudioPlaying = isPlaying
  },
  { immediate: true },
)

const isBook = computed(() => {
  return props.doc.doc_type === 'book'
})

function togglePlayback(): void {
  playerSession.value.togglePlayback({
    entryPoint: 'player',
    referrerPageName: route.fullPath,
  })
}
</script>
